/**
 * Redefinir a senha de alguém da equipe (#1167, ADR-0033).
 *
 * A janela diz o que acontece (a senha atual deixa de valer, e quem estiver
 * conectado sai) e pede a justificativa, que fica na trilha. Depois vem o
 * step-up que o servidor exige: o diálogo dele abre por cima, e a redefinição
 * é refeita sozinha ao fim, com a mesma justificativa.
 *
 * O botão fica travado enquanto o pedido está no ar. Dois pedidos ao mesmo
 * tempo trocariam a senha duas vezes, e a tela poderia mostrar por último a
 * que o login já tinha trocado.
 *
 * A recusa "só a Cliqfy" (403 sem step-up) e o login fora (503) chegam sem o
 * motivo: a API não solta o detalhe desses dois códigos. As frases vêm daqui.
 */

import { useState, type ReactNode } from 'react';
import type { Pessoa, RedefinicaoDeSenha } from '@cliqcrm/contracts';
import { Botao, Campo, Dialogo } from '@/shared/ui';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import type { UsoDeStepUp } from '@/features/acesso/confirmar-identidade';

const MINIMO_DA_JUSTIFICATIVA = 10;

export function RedefinirSenha({
  pessoa,
  stepUp,
  aoFechar,
  aoRedefinir,
}: {
  pessoa: Pessoa;
  stepUp: UsoDeStepUp;
  aoFechar: () => void;
  aoRedefinir: (resultado: RedefinicaoDeSenha) => void;
}): ReactNode {
  const api = useApi();
  const [justificativa, definirJustificativa] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [recusa, definirRecusa] = useState<string | null>(null);

  const falhou = (erro: unknown) =>
    definirRecusa(
      recadoDeErro(erro, {
        generica: 'Não foi possível redefinir a senha. Tente de novo.',
        porCodigo: {
          SEM_PERMISSAO:
            'A senha desta pessoa só a equipe da Cliqfy redefine. Peça a ela pelo suporte.',
          ENGINE_INDISPONIVEL:
            'O login não respondeu, e a senha pode continuar a mesma. Tente de novo: cada ' +
            'tentativa gera uma senha nova.',
        },
      }),
    );

  const redefinir = async () => {
    if (!api || enviando) return;
    definirRecusa(null);
    definirEnviando(true);
    const texto = justificativa.trim();
    try {
      await stepUp.comStepUp(
        async () => {
          aoRedefinir(
            await api.redefinirSenhaDaPessoa({
              caminho: { id: pessoa.id },
              corpo: { justificativa: texto },
            }),
          );
        },
        pessoa.id,
        falhou,
      );
    } catch (erro: unknown) {
      falhou(erro);
    } finally {
      definirEnviando(false);
    }
  };

  const curta = justificativa.trim().length < MINIMO_DA_JUSTIFICATIVA;

  return (
    <Dialogo
      titulo={`Redefinir a senha de ${pessoa.nome}`}
      aoFechar={aoFechar}
      consequencia
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            desabilitado={curta || enviando || api === null}
            motivo={
              api === null
                ? 'Sem conexão agora. Tente de novo em instantes.'
                : curta
                  ? 'Escreva ao menos dez caracteres de justificativa.'
                  : 'Redefinindo…'
            }
            onClick={() => void redefinir()}
          >
            {enviando ? 'Redefinindo…' : 'Redefinir senha'}
          </Botao>
        </>
      }
    >
      <p>
        A senha atual de {pessoa.nome} deixa de valer agora, e quem estiver conectado com ela sai.
        Você recebe uma senha provisória para passar a {pessoa.nome}, que vai ser obrigada a
        trocá-la no próximo acesso.
      </p>

      <Campo
        rotulo="Por quê?"
        type="text"
        value={justificativa}
        maxLength={500}
        autoComplete="off"
        ajuda="Fica na trilha, com o seu nome. A senha não fica."
        onChange={(e) => definirJustificativa(e.currentTarget.value)}
      />

      {recusa && (
        <p className="dialogo__erro" role="alert">
          {recusa}
        </p>
      )}
    </Dialogo>
  );
}
